import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { MatSelectionList } from '@angular/material/list';
import { Store } from '@ngrx/store';
import { merge, Subject } from 'rxjs';
import { filter, mergeMap, switchMap, tap } from 'rxjs/operators';
import * as MeetingAttendeesActions from '../+state/meeting-attendees/meeting-attendees.actions';
import { MeetingAttendeesPartialState } from '../+state/meeting-attendees/meeting-attendees.reducer';
import { getMeeting, getMeetingId } from '../+state/meeting-attendees/meeting-attendees.selectors';
import { MeetingAttendeesService } from '../meeting-attendee.service';
import { MeetingCheckInSteps } from '../meeting-check-in-steps';

@Component({
  selector: 'mstar-meeting-attendee-check-in',
  templateUrl: './meeting-attendee-check-in.component.html',
  styleUrls: ['./meeting-attendee-check-in.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MeetingAttendeeCheckInComponent implements OnDestroy {

  // == STATE =====================================================

  processingCheckIns = new Subject<boolean>();
  processingCheckIns$ = this.processingCheckIns.asObservable();

  markArrived = new Subject<number[]>();
  markArrived$ = this.markArrived.asObservable();

  meetingId$ = this.store.select(getMeetingId);
  meeting$ = this.store.select(getMeeting);

  // == ACTIONS ===================================================

  getAttendees = new Subject();
  getAttendees$ = this.getAttendees.asObservable();

  // == INTERMEDIATE ==============================================  

  attendees$ = this.meetingId$.pipe(
    filter(meetingId => !!meetingId),
    switchMap(meetingId => this.meetingAttendeeService.getAttendees(meetingId))
  );

  // == SIDE EFFECTS ==============================================  

  onMarkArrived$ = this.markArrived$.pipe(
    tap(() => this.processingCheckIns.next(true)),
    mergeMap(attendeeIds => this.meetingAttendeeService.checkInAttendee(attendeeIds)),
    tap(() => {
      this.processingCheckIns.next(false);
      this.store.dispatch(MeetingAttendeesActions.checkedIn());
    })
  );

  // == SUBSCRIPTION ==============================================

  subscription = merge(
    this.onMarkArrived$
  ).subscribe();

  // == LIFECYCLE =================================================

  constructor(private meetingAttendeeService: MeetingAttendeesService,
              private store: Store<MeetingAttendeesPartialState>) { }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  // == HELPERS ===================================================

  checkIn(selectionList: MatSelectionList) {
    const selectedOptions = selectionList.selectedOptions.selected.map(o => o.value);
    this.markArrived.next(selectedOptions);
  }

  unknownAttendee() {
    this.store.dispatch(MeetingAttendeesActions.goToStep({ 
      step: MeetingCheckInSteps.attendeeRegistration 
    }))
  }

  back() {
    this.store.dispatch(MeetingAttendeesActions.goToStep({ 
      step: MeetingCheckInSteps.meetingCode 
    }))
  }
}
